import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskLines } from "./Reveal";

const HERO_IMG = "/ig/kombi.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 md:pt-36 pb-16"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:120px_120px]" />

      <motion.div
        style={{ y: imgY, rotate: imgRotate, scale: imgScale }}
        className="absolute right-[-6%] top-[8%] w-[70vw] max-w-[820px] aspect-[3/4] hidden md:block"
      >
        <div className="relative w-full h-full overflow-hidden">
          <img
            src={HERO_IMG}
            alt="Peça personalizada — Acervo 3D"
            className="w-full h-full object-cover"
            style={{ filter: "grayscale(15%) contrast(1.05) brightness(0.82)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[rgba(8,8,8,0.35)] to-[var(--bg)]" />
          <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_30%_40%,rgba(232,184,168,0.14),transparent_70%)]" />
        </div>
      </motion.div>

      <div className="md:hidden absolute right-[-20%] top-[12%] w-[110vw] aspect-[3/4] opacity-70">
        <img
          src={HERO_IMG}
          alt=""
          className="w-full h-full object-cover"
          style={{ filter: "grayscale(15%) contrast(1.05) brightness(0.7)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(8,8,8,0.3)] to-[var(--bg)]" />
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 h-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-4 mb-14 md:mb-24"
        >
          <span className="w-10 h-px bg-[var(--ink-2)]" />
          <span className="font-mono-tech text-[11px] text-[var(--ink-2)]">
            Ateliê 3D · Peças personalizadas
          </span>
        </motion.div>

        <motion.h1
          style={{ y: titleY }}
          className="font-serif-display text-[18vw] md:text-[13vw] leading-[0.82] tracking-[-0.04em]"
          data-testid="hero-title"
        >
          <MaskLines lines={["Itens que"]} delay={0.35} />
          <div className="flex items-baseline gap-[0.15em] md:gap-[0.2em]">
            <MaskLines
              lines={[
                <span key="k" className="italic text-[var(--accent)]">
                  contam
                </span>,
              ]}
              delay={0.5}
            />
            <MaskLines lines={["histórias."]} delay={0.65} />
          </div>
        </motion.h1>

        <div className="mt-14 md:mt-24 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-6 md:col-start-1">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif-display text-2xl md:text-4xl max-w-[38ch] tracking-tight text-[var(--ink)] leading-[1.1]"
            >
              Transformamos <em className="italic text-[var(--ink-2)]">ideias, sentimentos e memórias</em> em peças
              únicas — modernas, delicadas e cheias de personalidade.
            </motion.p>
          </div>

          <div className="col-span-12 md:col-span-3 md:col-start-10 flex md:justify-end">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col gap-3"
            >
              <span className="font-mono-tech text-[10px] text-[var(--ink-3)]">
                Scroll →
              </span>
              <div className="flex gap-2">
                <span className="w-6 h-px bg-[var(--ink-2)]" />
                <span className="w-3 h-px bg-[var(--ink-3)]" />
                <span className="w-1.5 h-px bg-[var(--ink-3)]" />
              </div>
              <span className="font-mono-tech text-[10px] text-[var(--ink-3)]">
                Ateliê · MMXXVI
              </span>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 md:bottom-10 inset-x-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-end justify-between gap-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
            className="font-mono-tech text-[10px] text-[var(--ink-3)] flex items-center gap-4"
          >
            <span>Feito à mão</span>
            <span className="w-6 h-px bg-[var(--ink-3)]" />
            <span>Peças únicas</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.1, duration: 1 }}
            className="font-mono-tech text-[10px] text-[var(--ink-3)] hidden sm:flex items-center gap-4"
          >
            <span>Presentes</span>
            <span className="w-6 h-px bg-[var(--ink-3)]" />
            <span>Homenagens</span>
            <span className="w-6 h-px bg-[var(--ink-3)]" />
            <span>Decoração</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
